import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, Wrench, Trash2 } from 'lucide-react';
import { api, parseApiError, type AdminRecord } from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { PageWrapper } from '../components/PageWrapper';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';

// TODO(role-spine): AdminTiers is vestigial now that HELPER + platform-ADMIN
// tiers are retired — candidate for removal.
//
// ─────────────────────────────────────────────────────────────────────────────
// AdminTiers — HISTORICALLY the one surface that could appoint a platform
// admin tier (FOUNDER / HELPER), plus the founder-only review/revoke list for
// existing helpers.
//
// ADMIN tier retired (2026-07-17): the platform model became FOUNDER + HELPER
// only — ADMIN overlapped confusingly with HELPER and with org-level Admin.
//
// HELPER tier retired too (2026-07-24, role-spine follow-up, Directive 1):
// the platform 'helper' ROLE is now folded into admin. Nobody held it at
// retirement (same precondition as the ADMIN retirement), so the backend
// (POST /admin/set-tier) now 400s any attempt to grant EITHER retired tier —
// FOUNDER is the only value it still accepts, and FOUNDER status itself is
// authoritative from a fixed 2-person SSM list, never this UI. The appoint
// picker below is therefore GONE (there is nothing left worth appointing
// through it) — see AssignTierSection's honest empty state.
//
// TIER_COLORS and the "current admins" list below deliberately still handle
// ADMIN and HELPER rows — defensive rendering for any legacy/historical
// record, never appointable going forward.
//
// The founder-only review/revoke list for existing HELPER rows is KEPT —
// POST /admin/revoke-tier still works as the founder's cleanup lever for a
// stray pre-retirement HELPER row (surfaced by the backfill script's B5
// human-review step). Revoke is a manual, one-at-a-time founder action,
// never automatic.
//   - This page (and its embedded AdminTiersPanel form) is reachable only
//     from the Founder section of the sidebar (Sidebar.tsx GROUPS 'Founder').
//   - The backend enforces founder-only independently either way — POST
//     /admin/set-tier and POST /admin/revoke-tier both 403 any non-founder,
//     including a platform admin or an org owner (see
//     helperTierLockdown.int.test.ts). This page-level guard is
//     defense-in-depth, matching the discipline the old embedded section
//     already had (`if (me.role !== 'founder') return null`).
// ─────────────────────────────────────────────────────────────────────────────

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// UserLookup/AdminPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

const TIER_COLORS: Record<string, string> = {
  FOUNDER: 'text-yellow-400',
  ADMIN: 'text-accent-500',
  HELPER: 'text-sky-300',
};

// Was an interactive "Assign Tier" form (appoint FOUNDER or HELPER by Discord
// ID). Both retired now (role-spine follow-up, 2026-07-24, Directive 1: HELPER
// folded into admin; FOUNDER status is authoritative from a fixed 2-person SSM
// list, never appointable through this UI) — there is nothing left worth
// appointing, so the form is gone. Keeps the read-only "current admin-tier
// rows" list (still useful for audit — legacy FOUNDER/ADMIN/HELPER rows can
// exist) and renders an honest empty state instead of a broken picker.
function AssignTierSection({ admins, isLoading }: { admins: AdminRecord[] | undefined; isLoading: boolean }) {
  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <Crown className="h-4 w-4 text-yellow-400" /> Admin-Tier Rows
      </h2>

      {isLoading ? <PageSpinner /> : (admins && admins.length > 0) ? (
        <div className="mb-4 space-y-2">
          {admins.map(a => (
            <div key={a.pk} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2 text-sm">
              <span className="text-ink-body">
                {a.displayName ?? <span className="font-mono">{shortId(a.discordId)}</span>}
              </span>
              <span className={`font-semibold ${TIER_COLORS[a.tier] ?? 'text-ink-secondary'}`}>{a.tier}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mb-4 text-sm text-ink-meta">No admin-tier rows exist.</p>
      )}

      <div className="rounded-xl bg-white/[0.04] p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-meta">Assign Tier</p>
        <p className="mt-2 text-[11px] text-ink-meta">
          No appointable platform tiers. HELPER is retired (folded into admin,
          role-spine follow-up 2026-07-24) and FOUNDER status comes only from
          a fixed 2-person SSM list — neither is grantable through this UI.
        </p>
      </div>
    </Card>
  );
}

function HelperRow({ helper, onRevoked }: { helper: AdminRecord; onRevoked: () => void }) {
  const [error, setError] = useState('');

  const revokeMut = useMutation({
    mutationFn: () => api.admin.revokeTier(helper.discordId),
    onSuccess: onRevoked,
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const label = helper.displayName ?? shortId(helper.discordId);

  return (
    <div className="rounded-lg bg-white/[0.04] px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-ink-body">{label}</p>
          {helper.displayName && (
            <p className="font-mono text-[10px] text-ink-meta">{shortId(helper.discordId)}</p>
          )}
        </div>
        <button
          onClick={() => {
            if (window.confirm(`Revoke this legacy HELPER row for ${label}? The platform 'helper' role is retired and already grants nothing — this just cleans up the stray record. It doesn't touch any org/clan role they hold.`)) {
              revokeMut.mutate();
            }
          }}
          disabled={revokeMut.isPending}
          className="flex shrink-0 items-center gap-1 rounded-md bg-red-500/10 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/15 disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" /> {revokeMut.isPending ? '…' : 'Revoke'}
        </button>
      </div>
      <ErrorMsg msg={error} />
    </div>
  );
}

// Was "Current Helpers" — the platform 'helper' ROLE is retired 2026-07-24
// (role-spine follow-up, Directive 1), folded into admin. A row here now can
// only be a stray pre-retirement record (nobody held HELPER at retirement
// time); this stays as the founder's cleanup lever for one surfaced by the
// backfill script's B5 human-review step, per Directive 1.
function CurrentHelpersSection({ admins, isLoading }: { admins: AdminRecord[] | undefined; isLoading: boolean }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'admins'] });
  const helpers = (admins ?? []).filter(a => a.tier === 'HELPER');

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <Wrench className="h-4 w-4 text-sky-300" /> Legacy Helper Rows
        {helpers.length > 0 && (
          <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs font-normal">{helpers.length}</span>
        )}
      </h2>

      {isLoading ? (
        <PageSpinner />
      ) : helpers.length === 0 ? (
        <p className="text-sm text-ink-meta">No legacy HELPER rows — the platform 'helper' role is retired.</p>
      ) : (
        <div className="space-y-2">
          {helpers.map(h => (
            <HelperRow key={h.pk} helper={h} onRevoked={refresh} />
          ))}
        </div>
      )}
    </Card>
  );
}

// Reusable panel — the Admin-Tier-Rows + Legacy-Helper-Rows read-only/cleanup
// controls WITHOUT the page chrome (no PageWrapper/header/HelperCard).
// Rendered by the AdminTiers page AND inline as a collapsible dropdown on the
// Founder Admin page (Lisa, 2026-07-17), so the whole platform-role surface
// lives in one founder place. Every parent that mounts it is already
// founder-gated; the admins query it runs is founder-only server-side
// regardless.
export function AdminTiersPanel() {
  const { data: admins, isLoading: adminsLoading } = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: api.admin.admins,
  });

  return (
    <div className="space-y-6">
      <AssignTierSection admins={admins} isLoading={adminsLoading} />
      <CurrentHelpersSection admins={admins} isLoading={adminsLoading} />
    </div>
  );
}

export function AdminTiers() {
  const { data: me, isLoading: meLoading } = useQuery({ queryKey: ['me'], queryFn: api.me });

  if (meLoading) return <PageSpinner />;

  if (!me || me.role !== 'founder') {
    return (
      <Card className="text-center">
        <p className="text-gray-400">Founder access required.</p>
      </Card>
    );
  }

  return (
    <PageWrapper maxWidth="4xl" spacing="lg">
      <div className={cn('flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Crown className="h-7 w-7 text-yellow-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Tiers</h1>
          <p className="text-xs text-ink-meta">Founder-only — HELPER and platform ADMIN are both retired; nothing is appointable here any more.</p>
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <HelperCard
          title="What lives here"
          intro="The platform 'helper' role is retired (2026-07-24, folded into admin), same as the platform ADMIN tier before it (2026-07-17). This page no longer appoints anything — it's a read-only audit view plus a cleanup lever for any stray legacy row."
          points={[
            { text: 'Admin-Tier Rows lists every existing FOUNDER/ADMIN/HELPER row, read-only.' },
            { text: "Legacy Helper Rows lists any stray pre-retirement HELPER row, with a one-click revoke to clean it up. Revoking doesn't touch anyone's org or clan role." },
            { text: 'Nothing here is auto-revoked — cleanup is a manual, one-at-a-time founder action.' },
          ]}
        />
      </div>

      <div className={SETTLE} style={settleDelay(2)}>
        <AdminTiersPanel />
      </div>
    </PageWrapper>
  );
}
